import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { DataTable, PageHeader, Column } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import { FxVendor, FxCategory } from '@/types/api/finance';

type Props = {
  vendors: FxVendor[];
  categories: FxCategory[];
  isLoading: boolean;
  showForm: boolean;
  editingVendor: FxVendor | null;
  search: string;
  onSearchChange: (value: string) => void;
  onNewVendor: () => void;
  onEdit: (vendor: FxVendor) => void;
  onDelete: (vendor: FxVendor) => void;
  onSubmit: (data: any) => void;
  onCloseForm: () => void;
  isSubmitting: boolean;
};

const FinanceXVendorsComponent = function ({
  vendors,
  categories,
  isLoading,
  showForm,
  editingVendor,
  search,
  onSearchChange,
  onNewVendor,
  onEdit,
  onDelete,
  onSubmit,
  onCloseForm,
  isSubmitting,
}: Props): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState<FxVendor | null>(null);

  const columns: Column<FxVendor>[] = [
    {
      id: 'name',
      label: 'Name',
      render: (vendor) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {vendor.logoUrl ? (
            <Box
              component="img"
              src={vendor.logoUrl}
              alt={vendor.name}
              sx={{ width: 28, height: 28, borderRadius: '6px', objectFit: 'contain' }}
            />
          ) : (
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                backgroundColor: '#054AD9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {vendor.name.charAt(0).toUpperCase()}
            </Box>
          )}
          <span style={{ fontWeight: 600, fontSize: 13 }}>{vendor.displayName || vendor.name}</span>
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (vendor) =>
        vendor.category ? (
          <Chip
            label={vendor.category.name}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: `${vendor.category.color}20`,
              color: vendor.category.color ?? '#6B7280',
            }}
          />
        ) : (
          <span style={{ color: '#C5C5C7' }}>—</span>
        ),
    },
    {
      id: 'website',
      label: 'Website',
      render: (vendor) =>
        vendor.website ? (
          <Box
            component="a"
            href={vendor.website}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: '#054AD9',
              fontSize: 13,
              textDecoration: 'none',
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {vendor.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Box>
        ) : (
          <span style={{ color: '#C5C5C7' }}>—</span>
        ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '80px',
      render: (vendor) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(vendor);
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete(vendor);
            }}
            sx={{ color: '#FF5E54' }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ padding: '40px' }}>
      <PageHeader
        title="Vendors"
        actions={
          <TrioButton variantColor="blue" size="small" onClick={onNewVendor}>
            <AddIcon sx={{ fontSize: 18, mr: 0.5 }} /> Add Vendor
          </TrioButton>
        }
      />

      <DataTable<FxVendor>
        columns={columns}
        data={vendors}
        rowKey={(v) => v.id}
        isLoading={isLoading}
        searchPlaceholder="Search vendors..."
        searchFields={(v) => `${v.name} ${v.displayName ?? ''} ${v.category?.name ?? ''}`}
        emptyMessage="No vendors yet"
        emptyIcon={StorefrontIcon}
      />

      <Dialog open={showForm} onClose={onCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingVendor ? 'Edit Vendor' : 'Add Vendor'}
        </DialogTitle>
        <Formik
          initialValues={{
            name: editingVendor?.name ?? '',
            displayName: editingVendor?.displayName ?? '',
            website: editingVendor?.website ?? '',
            categoryId: editingVendor?.categoryId ?? '',
            notes: editingVendor?.notes ?? '',
          }}
          enableReinitialize
          onSubmit={(values) => {
            onSubmit({
              name: values.name,
              displayName: values.displayName || undefined,
              website: values.website || undefined,
              categoryId: values.categoryId || undefined,
              notes: values.notes || undefined,
            });
          }}
        >
          {({ values, handleChange }) => (
            <Form>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Field
                  as={TextField}
                  name="name"
                  label="Vendor Name"
                  required
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="displayName"
                  label="Display Name"
                  fullWidth
                  size="small"
                  helperText="Optional friendly name"
                  value={values.displayName}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="website"
                  label="Website"
                  fullWidth
                  size="small"
                  placeholder="https://example.com"
                  value={values.website}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="categoryId"
                  label="Category"
                  select
                  fullWidth
                  size="small"
                  value={values.categoryId}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  as={TextField}
                  name="notes"
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={values.notes}
                  onChange={handleChange}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCloseForm} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <TrioButton
                  type="submit"
                  variantColor="blue"
                  size="small"
                  disabled={!values.name || isSubmitting}
                >
                  {(() => {
                    if (isSubmitting) return <CircularProgress size={20} />;
                    return editingVendor ? 'Update' : 'Create';
                  })()}
                </TrioButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Vendor?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete &quot;{confirmDelete?.displayName || confirmDelete?.name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: '999px', textTransform: 'none' }}
            onClick={() => {
              if (confirmDelete) onDelete(confirmDelete);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanceXVendorsComponent;
